import React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Calendar } from './calendar';
import { cn } from '../../lib/utils';

export interface DateRange {
  from: Date;
  to: Date;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  className,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [tempRange, setTempRange] = React.useState<{ from?: Date; to?: Date }>({
    from: value.from,
    to: value.to,
  });

  const formatDateRange = (range: DateRange) => {
    const formatOptions: Intl.DateTimeFormatOptions = { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    };
    
    return `${range.from.toLocaleDateString('en-US', formatOptions)} - ${range.to.toLocaleDateString('en-US', formatOptions)}`;
  };

  const handleSelect = (selected: Date | { from?: Date; to?: Date } | undefined) => {
    if (selected && typeof selected === 'object' && 'from' in selected) {
      setTempRange(selected);
      
      // If both dates are selected, close the popover and apply
      if (selected.from && selected.to) {
        onChange({ from: selected.from, to: selected.to });
        setIsOpen(false);
      }
    }
  };

  const handleCancel = () => {
    setTempRange({ from: value.from, to: value.to });
    setIsOpen(false);
  };

  const handleClear = () => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 6);
    
    const newRange = {
      from: start,
      to: end,
    };
    
    setTempRange(newRange);
    onChange(newRange);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn('gap-2', className)}>
          <CalendarIcon className="h-4 w-4" />
          {formatDateRange(value)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="range"
          selected={tempRange}
          onSelect={handleSelect}
        />
        <div className="border-t p-3 flex gap-2">
          <Button onClick={handleClear} variant="outline" size="sm" className="flex-1">
            Reset
          </Button>
          <Button onClick={handleCancel} variant="ghost" size="sm" className="flex-1">
            Cancel
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

